import { useTranslation } from 'next-i18next'
import React from 'react'

import { StoreButtons } from './StoreButtons'
import s from './landing.module.css'
import { Persona } from './persona'

const NAV_LINKS: Array<[string, string]> = [
  ['#trailer', 'trailer'],
  ['#podcasts', 'podcasts'],
  ['#descoberta', 'discovery'],
  ['#desafiar', 'challenge'],
  ['#card360', 'card360'],
  ['#academias', 'gyms'],
  ['#manifesto', 'manifesto'],
  ['#suporte', 'support'],
]

/** Hero vermelho: nav, ghost OSS, título, botões das lojas e mockup do mapa. */
export function Hero({ persona }: { persona: Persona }) {
  const { t } = useTranslation('landing')

  return (
    <header
      className="bg-brand"
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      <nav className={s.nav} aria-label={t('gates:common.navAria')}>
        <a className={s.wordmark} href="#topo">
          Goodfellas
        </a>
        <div className={s.navLinks}>
          {NAV_LINKS.map(([href, key]) => (
            <a key={href} className={s.navl} href={href}>
              {t(`nav.${key}`)}
            </a>
          ))}
          <a
            className={`${s.btn} ${s.btnW}`}
            href="#baixar"
            style={{ padding: '12px 22px' }}
          >
            {t('nav.download')}
          </a>
        </div>
      </nav>

      <div
        className={s.ghost}
        style={{
          fontSize: 'clamp(200px,30vw,520px)',
          top: '40%',
          insetInlineStart: '-2%',
        }}
        aria-hidden="true"
      >
        OSS
      </div>

      <div
        id="topo"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 24,
          padding: '60px 7vw 110px',
          position: 'relative',
        }}
      >
        <div style={{ maxWidth: 760 }}>
          <p className={`${s.kick} text-white`}>
            <span className={s.kbar} />
            {t('hero.kicker')}
          </p>
          <h1
            className={`${s.d} ${s.h1} text-white`}
            style={{ margin: '26px 0 0' }}
          >
            {t('hero.title1')}
            <br />
            <span className={s.outW}>{t('hero.title2')}</span>
            <br />
            {t('hero.title3')}
          </h1>
          <p
            className={s.lead}
            style={{
              fontSize: 20,
              marginTop: 16,
              color: 'rgba(255,255,255,.85)',
            }}
          >
            {t('hero.lead')}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginTop: 34,
            }}
          >
            <StoreButtons />
            <span
              className={`${s.cond} text-white`}
              style={{ fontSize: 14, letterSpacing: '.14em' }}
            >
              {t('stores.free')}
            </span>
          </div>
        </div>

        <PhoneMock persona={persona} />
      </div>
    </header>
  )
}

/** Mockup de celular com mapa fake: pins, pulso, pill de busca e card do atleta. */
function PhoneMock({ persona }: { persona: Persona }) {
  const { t } = useTranslation('landing')

  return (
    <div
      style={{
        position: 'relative',
        paddingBlock: '20px 40px',
        paddingInline: '0 30px',
      }}
    >
      <div
        className={s.stripes}
        style={{
          position: 'absolute',
          width: 120,
          height: 90,
          insetInlineEnd: -10,
          top: -14,
          color: '#fff',
        }}
        aria-hidden="true"
      />
      <div className={s.phone} role="img" aria-label={t('hero.mapAria')}>
        <div className={s.scr}>
          <div className={s.mapbg} />
          <div
            className={s.road}
            style={{
              left: 0,
              right: 0,
              top: '34%',
              height: 14,
              transform: 'rotate(-7deg)',
            }}
          />
          <div
            className={s.road}
            style={{
              top: 0,
              bottom: 0,
              left: '58%',
              width: 12,
              transform: 'rotate(12deg)',
            }}
          />
          <div className={s.pillsrch}>
            {t('hero.nearYou')} · {persona.city}
          </div>
          <div className={s.pulse} style={{ left: 96, top: 206 }} />
          <div className={s.pin} style={{ left: 119, top: 229 }} />
          <div className={s.pin} style={{ left: 212, top: 150 }} />
          <div className={s.pin} style={{ left: 66, top: 330 }} />
          <div className={s.pin} style={{ left: 240, top: 300 }} />
          <div className={s.gympin} style={{ left: 150, top: 390 }}>
            {t('hero.gymPin')}
          </div>
          <div className={s.mcard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className={s.ava}>{persona.init}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  {persona.name}
                </div>
                <div style={{ fontSize: 13, color: '#7b7b7b' }}>
                  {t('hero.mapCardMeta')}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <div className={`${s.sbtn} ${s.sbr}`}>
                {t('hero.trainTogether')}
              </div>
              <div className={`${s.sbtn} ${s.sbo}`}>{t('hero.challenge')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/** Marquee OSS — duas cópias idênticas animadas até -50% (loop sem costura). */
export function Marquee() {
  const words = Array.from({ length: 16 })
  return (
    <div className={s.mq} aria-hidden="true">
      <div className={s.mqt}>
        {words.map((_, i) => (
          <span
            key={i}
            className={`${s.d} ${s.mqw} ${i % 2 === 0 ? s.outR : ''}`}
            style={i % 2 === 0 ? undefined : { color: '#EC1B2E' }}
          >
            OSS
          </span>
        ))}
      </div>
    </div>
  )
}
